/**
 * Inside the Dele Longevity Android app, hands the month so far to the app shell, which
 * shows it as one line on Today (Super App Phase 2). The shell offers `deleMoneySummary`
 * to this origin only; in a browser it does not exist and this does nothing. Demo Mode
 * never sends: its figures are synthetic. Renders nothing.
 */
import { useEffect } from 'react';
import { useApp } from './context';
import { shellSummary } from '../lib/shell';

interface ShellPort { postMessage(message: string): void }
const port = (): ShellPort | null => {
  const p = (window as unknown as { deleMoneySummary?: ShellPort }).deleMoneySummary;
  return p && typeof p.postMessage === 'function' ? p : null;
};

/** How often the figures are re-read while the app stays open (bank alerts arrive through the day). */
const EVERY_MS = 15 * 60_000;

export function ShellSummary() {
  const { repo, base } = useApp();
  const plan = base.openPlan;

  useEffect(() => {
    if (repo.isDemo || !port()) return;
    let stopped = false;
    const send = async () => {
      try {
        const rows = plan ? await repo.provisional(plan.month_start) : [];
        if (rows === null) return; // unknown: say nothing rather than something wrong
        const rules = rows.length ? await repo.payeeRules() : [];
        if (!stopped) port()?.postMessage(JSON.stringify(shellSummary(plan, rows, base.categories, rules, base.accounts)));
      } catch {
        // A failed read leaves Today showing the last summary with its own time; nothing to do here.
      }
    };
    void send();
    const timer = window.setInterval(() => void send(), EVERY_MS);
    const onShow = () => { if (document.visibilityState === 'visible') void send(); };
    document.addEventListener('visibilitychange', onShow);
    return () => { stopped = true; window.clearInterval(timer); document.removeEventListener('visibilitychange', onShow); };
  }, [repo, plan, base.categories, base.accounts]);

  return null;
}
